import { FeatureFlagAvailabilitiesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

/**
 * This hooks allows you to check the features you want easily, it will return an object
 * with the values you want
 *
 * Example:
 * useFeatures(['feature_1','feature_2'])
 *
 * Will return an object like
 * {
 *   loadingFeatures: false,
 *   has_feature_1: true,
 *   has_feature_2: false
 * }
 *
 * This makes easy to use in any place
 * const { has_dev_ai_agent } = useFeatures(['dev_ai_agent'])
 *
 * NOTE: This hook makes a request to the backend, so it's not optimal to use it on pages
 * where a GraphQL request is already being made. You should use 'checkFeatures' instead.
 */
export function useFeatures<K extends string>(
  keys: K[]
): Record<`has_${K}`, boolean> & Record<'loadingFeatures', boolean> {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading } = useQuery(FeatureFlagAvailabilitiesDocument, {
    variables: {
      companyId,
      featureNames: keys,
    },
  })
  const obj = checkFeatures(keys, data?.features?.featuresConnection.nodes)

  for (const key of keys) {
    obj[`has_${key}`] =
      data?.features?.featuresConnection.nodes.find((feature) => feature.name === key)?.enabled ??
      false
  }

  return {
    ...obj,
    loadingFeatures: loading,
  }
}

/**
 * This function allows you to check the features you want easily, it will return an object
 * with the values you want
 *
 * Example:
 * checkFeatures(['feature_1','feature_2'], graphqlData?.features.featuresConnection.nodes)
 *
 * Will return an object like
 * {
 *   has_feature_1: true,
 *   has_feature_2: false
 * }
 *
 * This makes easy to use in any place
 * const { has_dev_ai_agent } = checkFeatures(['dev_ai_agent'], graphqlData?.features.featuresConnection.nodes)
 *
 * NOTE: This method is useful when you already have a graphql query and you want to check one or more features
 * without making a new request to the backend adding this block to the current query you have
 *
 * features {
    featuresConnection(companyIds: [$companyId], names: ["dev_trainings"]) {
      nodes {
        enabled
        name
      }
    }
  }
 *
 * otherwise use 'useFeatures' which makes a request to the backend.
 */
export function checkFeatures<K extends string>(
  keys: K[],
  features: Array<{ name: string; enabled: boolean }> | undefined
): Record<`has_${K}`, boolean> {
  // use 'Object.create(null)' because 'const obj: Record<`has_${K}`, boolean> = {}' fails with
  // message "Type '{}' is not assignable to type 'Record<has_${K}, boolean>'" and
  // 'const obj = {} as Record<`has_${K}`, boolean>' fails
  // with message "Do not use as operator for type assertion"
  const obj: Record<`has_${K}`, boolean> = Object.create(null)
  for (const key of keys) {
    obj[`has_${key}`] = features?.find((feature) => feature.name === key)?.enabled ?? false
  }
  return obj
}
